import { useQuery } from '@tanstack/react-query';
import { getApiBaseUrl } from '@/lib/api-base-url';
import { authFetch } from '@/lib/auth-fetch';

export interface RollMonthPosition {
  cardRef: string;
  direction: 'Buy' | 'Sell';
  lots: number;
  mt: number;
  price: number | null;
  tradeDate: string | null;
  promptDate: string | null;
  metal: string;
  orderId: string | null;
  orderIds: string[];
  contractDesc: string;
}

export interface RollMonth {
  monthKey: string;
  label: string;
  isPast: boolean;
  netMt: number;
  dealCount: number;
  positionCount: number;
  // null on account-scoped snapshots (no internal account attribution yet).
  internalNetMt: number | null;
  delta: number | null;
  hasMismatch: boolean | null;
  positions: RollMonthPosition[];
}

export interface RollMonthsResponse {
  snapshotDate: string | null;
  months: RollMonth[];
  // 'all' = unscoped union; a list = scoped set. Present only while the
  // lender-hedging-allocation-enabled flag is on.
  accountScope?: string[] | 'all';
}

export class HedgingRollMonthsApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'HedgingRollMonthsApiError';
  }
}

async function fetchRollMonths(): Promise<RollMonthsResponse> {
  const response = await authFetch(`${getApiBaseUrl()}/hedging/roll-schedule/months`);
  if (!response.ok) {
    throw new HedgingRollMonthsApiError(
      `Request failed (${String(response.status)})`,
      response.status,
    );
  }
  return (await response.json()) as RollMonthsResponse;
}

export const HEDGING_ROLL_MONTHS_QUERY_KEY = ['hedging', 'roll-months'] as const;

export function useHedgingRollMonths() {
  return useQuery({
    queryKey: HEDGING_ROLL_MONTHS_QUERY_KEY,
    queryFn: fetchRollMonths,
  });
}
